/**
 * saturn-pricing-pdp.js — PDP pricing display (consumer of saturn-pricing.js)
 *
 * Reads the door-keyed config from #saturn-pricing-data, resolves the active door,
 * renders the door buy box that recomputes on quantity change, gates add-to-cart on
 * the door minimum, and stamps properties[_door] on the cart line.
 * Requires saturn-pricing.js (window.SaturnPricing) FIRST.
 *
 * ── ROADMAP #144 (2026-09-15) — the phone-first buy box, from Claude Design ──
 * One structure for Doors A / B / C, top to bottom (the theme's own blocks are
 * marked ⟨theme⟩; everything else is injected here):
 *
 *   eyebrow                         "Holiday cards · Letterpress" (per door)
 *   ⟨theme⟩ title
 *   format picker                   Single / Box of 8 / Bulk 50+ — LINKS between
 *                                   the design's door pages; the current door is
 *                                   the selected segment. Replaces the family row
 *                                   ("More from this design", dropped from the
 *                                   template) as the cross-door affordance.
 *   bridge note                     Door A arrival over the Box→A bridge only
 *   price row                       total · "N cards · $X each"
 *   ⟨theme⟩ quantity + Add to cart  one row (Eurus: show_atc_quantity)
 *   payment disclosure              "More payment options ⌄" — reveals the
 *                                   theme's Buy-with-Shop button + its link,
 *                                   which stay hidden until asked for (owner
 *                                   2026-09-14 late: keep them, behind a fold)
 *   pill row                        Door A: "Round up to N — $T · $R a card" pill
 *                                   (owner 2026-09-15: the rate rides INSIDE the
 *                                   pill), a pipe, then "Personalize this card" →
 *                                   the Personalize INTERSTITIAL (#158, 2026-09-16:
 *                                   mock image · story · steps · price at this
 *                                   quantity · one action on to the composer,
 *                                   Door D); at 50+ on A/C the Door D price at
 *                                   this quantity under the row
 *   volume pointer                  one line, "See volume pricing" opens the sheet
 *   box bridge line                 Door B only: "Get the same price without the box"
 *   ⟨theme⟩ description
 *
 * Plus a sticky Add-to-cart bar (title · "N cards · $T" · button) once the price
 * row scrolls under the header, seated ABOVE any bottom-fixed overlay (the email
 * tool's teaser) so neither hides the other; and the volume-pricing SHEET (bottom
 * sheet on phones, centred on desktop) whose rows set the quantity.
 *
 * What did NOT change: every price is computed from saturn.pricing_config by the
 * engine (#66 display == charge, resolveBillingDoor for the flip at 50); the
 * stock cap on what is OFFERED (never on what is CHARGED); the ?qty= bridge
 * preset; the Eurus stepper/Alpine quirks; the _door stamp; the #137 telemetry
 * vocabulary (door_pdp_loaded / ladder_opened / ladder_tier_selected /
 * roundup_click / sidedoor_click / door_atc_click — sidedoor `src` gains the
 * labels picker · pill · sheet beside panel · family; #158 adds `bridge`, the
 * interstitial's own continue action — pill − bridge is its drop-off).
 *
 * Earlier session history (3a/3b/3c, Session 3/5/6, #66/#70) lives in git; the
 * two-box "Volume savings" layout and the win-stating round-up label those
 * sessions built are superseded by this design.
 *
 * Theme-specific bits live in the adapters below. DAWN_ADAPTER is the default;
 * the pricing-data island opts a theme into another adapter by stamping
 * data-adapter (+ data-section-id) on #saturn-pricing-data — see
 * saturn-pricing-data.liquid. This file is CANONICAL in storefront/theme/assets;
 * the copy in storefront/theme-eurus/assets must stay byte-identical.
 */
(function () {
  "use strict";

  var DAWN_ADAPTER = {
    // Every selector is scoped to product-info (Dawn's main product section element).
    // Unscoped first-match selectors bite twice on a real PDP (found 2026-07-16):
    //  - a non-empty cart renders the cart DRAWER's .quantity__input BEFORE the PDP's,
    //    so the door floor was syncing the drawer line, not the buy box;
    //  - the hidden Shop Pay installments form (action /cart/add, no [name=add]) sits
    //    before the real product form, so properties[_door] was stamped into a form
    //    that never submits — cart lines arrived with no _door property.
    productFormSelector: 'product-info product-form form, product-form form',
    quantityInputSelector: 'product-info .quantity__input, product-info input[name="quantity"]',
    addButtonSelector: 'product-info [name="add"], product-info .product-form__submit, [name="add"]',
    injectPosition: 'beforebegin',
    collectionDoorMap: {
      'holiday': 'A',
      'holiday-cards': 'A',
      'bulk': 'C',
      'holiday-bulk': 'C',
      'personalization': 'D',
      'personalized': 'D',
      'holiday-personalization': 'D',
      'holiday-boxes': 'B'
    },
    // The theme's native price block below the product title. Hidden on Holiday
    // PDPs (our buy box owns price); confirm this matches your theme's element.
    nativePriceSelector: '.product__info-container .price',
    // The title block — the eyebrow is inserted directly before it.
    titleSelector: '.product__info-container .product__title',
    // The sticky header element, for the sticky-bar show threshold.
    headerSelector: 'sticky-header, .shopify-section-header',
    // The collection slug that surfaces the Bulk (Door C) view of this same
    // product — used to build the picker's Bulk segment. Door A and Door C are
    // the same G-pool product, so this is a context switch, not a cross-product
    // bridge. MUST be the real collection handle (confirmed `holiday-bulk`).
    bulkCollection: 'holiday-bulk',
    resolve: function () {
      var form = $(DAWN_ADAPTER.productFormSelector);
      var qtyInput = $(DAWN_ADAPTER.quantityInputSelector);
      var addBtn = $(DAWN_ADAPTER.addButtonSelector);
      // Panel above the quantity block when the template renders one, else
      // above the form; the after-container follows the whole product-form.
      var qtyBlock = qtyInput ? qtyInput.closest('.product-form__quantity, .product-form__input--quantity') : null;
      var formHost = form ? (form.closest('product-form') || form) : null;
      return {
        form: form, qtyInput: qtyInput, addBtn: addBtn,
        anchor: qtyBlock || formHost,
        afterAnchor: formHost,
        payWrap: form ? form.querySelector('.shopify-payment-button') : null
      };
    }
  };

  // Eurus (10.1.0, BSS Commerce — prod Retail). Alpine + Tailwind markup; every
  // element carries a section-scoped id, so the island passes section.id and we
  // resolve by id instead of by DOM-order selectors (Eurus renders sibling forms
  // — sticky ATC, variant picker — that share or shadow the main form's id, so
  // "first match in document" is not safe here).
  function eurusAdapter(sectionId) {
    var sid = sectionId || '';
    return {
      collectionDoorMap: DAWN_ADAPTER.collectionDoorMap,
      injectPosition: 'beforebegin',
      bulkCollection: DAWN_ADAPTER.bulkCollection,
      // #price-S is the price line; hide its whole block so no empty margin is
      // left behind (init walks up via nativePriceClosest when present).
      nativePriceSelector: '#price-' + sid,
      nativePriceClosest: '.x-block-price',
      // The section-level title (show_product_name) sits in a wrapper div that
      // carries the reveal animation's overflow-hidden — insert before the
      // wrapper, not inside it.
      titleSelector: '#ProductInfo-' + sid + ' .product-title',
      titleClosest: '.mb-3',
      headerSelector: '#sticky-header-content',
      // Eurus steppers mutate the input via Alpine x-model — the value changes
      // with NO input/change event on the element. The only signal is this
      // document-level CustomEvent from quantity-selector.js updatePrice().
      qtyChangedEvent: 'quantity:changed-' + sid,
      resolve: function () {
        var addBtn = document.getElementById('x-atc-button-' + sid);
        // The form that will actually submit is the one wrapping the ATC
        // button — never a bare id lookup (Eurus can render a second form
        // with the same id from the variant-picker block).
        var form = addBtn ? addBtn.closest('form') : null;
        // #144: the inline at-ATC input (show_atc_quantity) is the primary
        // quantity on the Holiday template — one row, stepper + button. The
        // standalone block (#Quantity-S) stays the fallback for a template
        // that still renders it.
        var qtyInput = document.getElementById('Quantity-atc-' + sid) ||
          document.getElementById('Quantity-' + sid);
        var buyBlock = form ? form.closest('.x-block-buy-buttons') : null;
        // Panel goes above the quantity block when present, else above the
        // whole buy-buttons block.
        var anchor = document.getElementById('quantity-selector-' + sid) || buyBlock || form;
        return {
          form: form, qtyInput: qtyInput, addBtn: addBtn, anchor: anchor,
          afterAnchor: buyBlock || form,
          payWrap: document.getElementById('x-payment-button-' + sid)
        };
      }
    };
  }

  // Resolved once in init() from the island's data-adapter attribute; every
  // consumer below reads ADAPTER, never a theme adapter directly.
  var ADAPTER = DAWN_ADAPTER;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function warn(msg) { if (window.console) console.warn('[saturn-pricing] ' + msg); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  // "Fifty" for the standard floor, the numeral otherwise — copy, not arithmetic.
  function floorWord(n) { return n === 50 ? 'Fifty' : String(n); }
  var PEN_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>';
  var PLUS_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>';
  var CHEV_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>';
  var X_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';

  function readConfig() {
    var el = document.getElementById('saturn-pricing-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { warn('could not parse pricing config: ' + e); return null; }
  }

  function readFamily() {
    var el = document.getElementById('saturn-family-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { warn('could not parse family data: ' + e); return null; }
  }

  // Reachability of this product's own doors (e.g. { "C": true } when the `bulk`
  // tag is on). Absent island or missing key => treat as not reachable.
  function readDoors() {
    var el = document.getElementById('saturn-doors-data');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; }
    catch (e) { warn('could not parse doors data: ' + e); return {}; }
  }

  function resolveDoor(config) {
    var m = window.location.pathname.match(/\/collections\/([^\/]+)\//);
    if (m) {
      var door = ADAPTER.collectionDoorMap[m[1]];
      if (door && config.doors[door]) return door;
    }
    return (config.doors[config.defaultDoor]) ? config.defaultDoor : Object.keys(config.doors)[0];
  }

  function injectStyles() {
    if (document.getElementById('saturn-pricing-styles')) return;
    var s = document.createElement('style');
    s.id = 'saturn-pricing-styles';
    // Design tokens (Saturn Press system, from the #144 handoff): ink #010101,
    // secondary #2B2B30, tertiary #6B6B72, link #6B7DB9, paper #FBFAF6, rule
    // rgba(1,1,1,.12). Sizes are explicit px so they never inherit a rem base
    // (Eurus root is 16px, Dawn's 62.5% — the same rem renders 1.6× apart).
    // Fonts inherit the theme's own (Garamond on retail). No Dawn tokens
    // (rgb(var(--color-*)) invalidates to transparent on Eurus). Radius 0
    // everywhere except pills.
    s.textContent = [
      '@keyframes spFade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}',
      '.sp-fade{animation:spFade 240ms cubic-bezier(.22,.61,.36,1)}',
      '.saturn-holiday-pdp .sp-eyebrow{font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:#6B6B72;margin:0 0 6px;line-height:1.4}',
      // ── the panel (above quantity + Add to cart) ───────────────────────────
      '.saturn-pricing{width:100%;margin:0 0 12px;font-size:15px;line-height:1.5;color:#010101;text-align:left}',
      '.sp-picker{display:flex;border:1px solid #010101;height:40px;margin:0 0 14px;font-size:12px;letter-spacing:.14em;text-transform:uppercase;line-height:1}',
      '.sp-picker__seg{flex:1 1 0;min-width:0;display:flex;align-items:center;justify-content:center;padding:0 6px;border-left:1px solid #010101;color:#010101;background:transparent;text-decoration:none;white-space:nowrap;transition:background 240ms cubic-bezier(.22,.61,.36,1),color 240ms cubic-bezier(.22,.61,.36,1)}',
      '.sp-picker__seg:first-child{border-left:0}',
      'a.sp-picker__seg:hover{background:rgba(1,1,1,.06);color:#010101;text-decoration:none}',
      '.sp-picker__seg.is-current{background:#010101;color:#fff}',
      '.sp-note{font-size:14px;color:#6B6B72;margin:0 0 12px}',
      '.sp-price{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin:0 0 12px}',
      '.sp-price__total{font-size:24px;line-height:1.1;font-weight:400}',
      '.sp-price__meta{font-size:14px;color:#6B6B72;text-align:right}',
      '@media (min-width:990px){.sp-price__total{font-size:28px}.sp-price__meta{font-size:15px}}',
      '.saturn-pricing__error{color:#7A2E2E;font-size:14px;font-weight:600;margin:0 0 8px}',
      // ── the after-container (below Add to cart) ───────────────────────────
      '.saturn-pricing--more{width:100%;margin:0 0 14px;font-size:14px;line-height:1.5;color:#010101;text-align:left}',
      '.sp-pay{margin:10px 0 0;text-align:center}',
      '.sp-pay__toggle{display:inline-flex;align-items:center;gap:6px;background:none;border:0;padding:0;font:inherit;font-size:14px;color:#6B7DB9;cursor:pointer;border-radius:0}',
      '.sp-pay__toggle:hover{color:#010101;text-decoration:underline}',
      // The pill row: Round up · | · Personalize, one line where they fit, the
      // pipe shown only while both pills share a line (layoutPills), stacked
      // with a small gap otherwise.
      '.sp-pills{display:flex;flex-wrap:wrap;align-items:center;gap:8px 0;margin:14px 0 0;font-size:14px}',
      '.sp-pills__sep{color:rgba(1,1,1,.3);padding:0 12px;font-size:15px;line-height:1}',
      '.sp-pills__sep[hidden]{display:none}',
      // Pills FLEX to fill their row — sharing it when they fit side by side,
      // each full-width when they stack (owner 2026-09-15).
      '.sp-pills>[data-roundup],.sp-pills>[data-personalize]{flex:1 1 auto;min-width:0;display:flex}',
      '.sp-pills>[hidden]{display:none}',
      '.sp-pills .sp-pill__btn{flex:1 1 auto;width:100%;justify-content:center}',
      '.sp-hint{font-size:14px;color:#6B6B72;margin:12px 0 0;padding:0 0 14px;border-bottom:1px solid rgba(1,1,1,.12)}',
      '.sp-hint--bridge{margin:0;padding:12px 0 14px}',
      '.sp-link{background:none;border:0;border-radius:0;padding:0;font:inherit;font-size:inherit;color:#6B7DB9;cursor:pointer;text-decoration:none}',
      '.sp-link:hover{color:#010101;text-decoration:underline}',
      '.sp-pill__btn{display:inline-flex;align-items:center;gap:8px;height:36px;padding:0 16px;border:1px solid #010101;border-radius:999px;background:none;color:#010101;font:inherit;font-size:15px;line-height:1;text-decoration:none;white-space:nowrap;flex:none;cursor:pointer;transition:background 240ms cubic-bezier(.22,.61,.36,1),color 240ms cubic-bezier(.22,.61,.36,1)}',
      '.sp-pill__btn:hover{background:#010101;color:#fff;text-decoration:none}',
      '.sp-pill__btn svg{width:14px;height:14px;flex:none}',
      '.sp-pill__price{flex-basis:100%;color:#6B6B72;margin-top:-2px}',
      // ── sticky Add-to-cart bar ────────────────────────────────────────────
      '.sp-sticky{position:fixed;left:0;right:0;bottom:0;z-index:45;height:64px;box-sizing:border-box;background:#FBFAF6;color:#010101;border-top:1px solid rgba(1,1,1,.12);display:flex;align-items:center;gap:12px;padding:0 20px;animation:spFade 240ms cubic-bezier(.22,.61,.36,1)}',
      '.sp-sticky[hidden]{display:none}',
      '.sp-sticky__text{flex:1 1 auto;min-width:0}',
      '.sp-sticky__title{font-style:italic;font-size:15px;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
      '.sp-sticky__meta{font-size:13px;color:#6B6B72;line-height:1.3}',
      '.sp-sticky__atc{flex:none;height:44px;padding:0 22px;background:#BAC2E5;color:#010101;font:inherit;font-size:16px;border:0;border-radius:999px;cursor:pointer;white-space:nowrap}',
      '.sp-sticky__atc:hover{background:#A7B2E7}',
      '.sp-sticky__atc[disabled]{opacity:.5;cursor:default}',
      // ── volume-pricing sheet (bottom sheet on phones, centred on desktop) ──
      '.saturn-tiers-overlay{position:fixed;inset:0;z-index:60;display:none;align-items:flex-end;justify-content:center;background:rgba(1,1,1,.4);padding:0}',
      '.saturn-tiers-overlay.saturn-open{display:flex}',
      // Solid paper only: a var()-based background invalidates to TRANSPARENT
      // on Eurus (UAT finding 2026-07-31: see-through tiers modal).
      '.saturn-tiers-modal{position:relative;background:#FBFAF6;color:#010101;width:100%;max-height:90vh;overflow-y:auto;box-sizing:border-box;padding:20px 20px 40px;font-size:15px;line-height:1.5;text-align:left;animation:spFade 240ms cubic-bezier(.22,.61,.36,1)}',
      '@media (min-width:768px){.saturn-tiers-overlay{align-items:center;padding:24px}.saturn-tiers-modal{width:480px;max-width:100%;padding:24px 28px 28px}}',
      '.sp-sheet__head{display:flex;justify-content:space-between;align-items:center;margin:0 0 6px}',
      '.sp-sheet__title{margin:0;font-size:26px;font-weight:500;line-height:1.1;font-style:normal}',
      '.sp-sheet__x{background:none;border:0;border-radius:0;padding:4px;cursor:pointer;display:flex;color:#010101}',
      '.sp-sheet__intro{font-size:15px;color:#2B2B30;margin:0 0 16px}',
      '.sp-sheet__row{display:grid;grid-template-columns:1fr auto auto;gap:16px;padding:12px 0;border-top:1px solid rgba(1,1,1,.12);font-size:16px;align-items:baseline}',
      '.sp-sheet__each{color:#6B6B72;white-space:nowrap}',
      '.sp-sheet__go{min-width:96px;text-align:right;font-size:14px}',
      '.sp-sheet__go[disabled]{color:#6B6B72;cursor:default;text-decoration:none}',
      '.sp-sheet__foot{border-top:1px solid rgba(1,1,1,.12);padding-top:14px;font-size:14px;color:#6B6B72}',
      // ── Personalize interstitial (#158): the sheet's container, its own body ─
      // A flex column (the #138 story-modal pattern): the story scrolls inside,
      // the price row and the continue action stay pinned at the foot — on a
      // phone the mock image would otherwise push the deal below the sheet's
      // fold. The addendum's three-step list was dropped (owner 2026-09-16:
      // an aid for later composer-PDP work, not for this bridge).
      '.sp-bridge{display:flex;flex-direction:column;padding:0;overflow:hidden}',
      '.sp-bridge__body{flex:1 1 auto;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:20px 20px 16px}',
      '.sp-bridge__foot{flex-shrink:0;padding:14px 20px calc(20px + env(safe-area-inset-bottom));border-top:1px solid rgba(1,1,1,.12);background:#FBFAF6}',
      '@media (min-width:768px){.sp-bridge__body{padding:24px 28px 16px}.sp-bridge__foot{padding:14px 28px 28px}}',
      '.sp-bridge__head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}',
      '.sp-bridge__title{margin:0;font-size:30px;font-weight:500;font-style:italic;line-height:1.05}',
      '.sp-bridge__title em{font-style:italic}',
      '.sp-bridge__fig{margin:16px 0 0;background:#F4F2EC;border:1px solid rgba(1,1,1,.12)}',
      '.sp-bridge__fig img{display:block;width:100%;height:auto}',
      '.sp-bridge__desc{font-size:15px;line-height:1.55;color:#2B2B30;margin:12px 0 0;text-wrap:pretty}',
      '.sp-bridge__price{display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin:0}',
      '.sp-bridge__total{font-size:24px;line-height:1.1;font-weight:400}',
      '.sp-bridge__meta{font-size:14px;color:#6B6B72;text-align:right}',
      '.sp-bridge__note{font-size:14px;line-height:1.5;color:#6B6B72;margin:6px 0 0}',
      '.sp-bridge__go{display:flex;align-items:center;justify-content:center;width:100%;height:48px;box-sizing:border-box;margin:16px 0 0;background:#BAC2E5;color:#010101;font:inherit;font-size:17px;line-height:1;border:0;border-radius:999px;text-decoration:none;cursor:pointer;transition:background 240ms cubic-bezier(.22,.61,.36,1),transform 240ms cubic-bezier(.22,.61,.36,1)}',
      '.sp-bridge__go:hover{background:#A7B2E7;color:#010101;text-decoration:none}',
      '.sp-bridge__go:active{transform:translateY(1px)}',
      // ── Eurus buy-buttons block: one row, stepper + pill button ───────────
      '.saturn-holiday-pdp .x-block-buy-buttons{margin-bottom:0}',
      '.saturn-holiday-pdp .x-block-buy-buttons .flex.gap-2{gap:10px;align-items:stretch}',
      '.saturn-holiday-pdp .x-block-buy-buttons .flex.gap-2>div:first-child{margin-bottom:0;border-color:#010101;border-radius:0;height:48px;box-sizing:border-box}',
      '.saturn-holiday-pdp .x-block-buy-buttons .flex.gap-2>button{flex:1 1 auto;min-width:0;height:48px;box-sizing:border-box}',
      // ── Dawn (dev/ilp): sticky info column + compact 2-up gallery, desktop ──
      // SPECIFICITY GOTCHA (found live 2026-07-15): Dawn's own first-child rule
      // outweighs a plain selector; the rule below is deliberately over-qualified.
      '@media (min-width:990px){',
      '  .saturn-holiday-pdp .product__info-wrapper{position:sticky;top:1.5rem;align-self:flex-start}',
      '  .saturn-holiday-pdp .product__media-list{display:flex;flex-wrap:wrap;gap:.6rem}',
      '  .saturn-holiday-pdp .product__media-list>.product__media-item{width:calc(50% - .3rem)}',
      '  .saturn-holiday-pdp .product:not(.product--columns) .product__media-list .product__media-item:first-child,',
      '  .saturn-holiday-pdp .product:not(.product--columns) .product__media-list .product__media-item--full{width:calc(50% - .3rem)}',
      '}'
    ].join('');
    document.head.appendChild(s);
  }

  function setDoorProperty(form, door) {
    var input = form.querySelector('input[name="properties[_door]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_door]';
      form.appendChild(input);
    }
    input.value = door;
  }

  // Door D test, delegated to the engine's canonical rule (`personalized: true`,
  // or a legacy setup fee — ROADMAP #136, 2026-09-10). The inline fallback only
  // matters for a browser holding a cached pre-#136 saturn-pricing.js.
  function isPersonalize(cfg) {
    var E = window.SaturnPricing;
    if (E && E.isPersonalizeDoor) return E.isPersonalizeDoor(cfg);
    return !!cfg && cfg.model === 'volume_tiers' && (cfg.personalized === true || !!cfg.setupFeeCents);
  }

  // The Bulk door = plain volume tiers (not the Personalize door).
  function isBulkDoor(cfg) {
    return !!cfg && cfg.model === 'volume_tiers' && !isPersonalize(cfg);
  }

  // The plain tier ladder a config carries (Door C on this catalog), or null.
  function findTierCfg(config) {
    if (!config || !config.doors) return null;
    for (var k in config.doors) {
      if (!Object.prototype.hasOwnProperty.call(config.doors, k)) continue;
      if (isBulkDoor(config.doors[k])) return config.doors[k];
    }
    return null;
  }

  // ── Volume-pricing sheet ────────────────────────────────────────────────────
  // Two faces of one sheet. 'bulk' (the Bulk page, or Door A once the quantity
  // is at the tier floor): the Door C ladder, each row SETS the quantity and
  // closes. 'single' (Door A below the floor, Door B): the three rungs a
  // single-card buyer can stand on — singles, a set of 8, the bulk floor —
  // with "Round up to 8" and "Order in bulk" as the row actions. No red "save"
  // figures anywhere (the data package flagged them as reading like a second
  // price). Tiers whose MINIMUM exceeds the stock cap are unfillable offers and
  // are not rendered; a tier whose range merely extends past stock stays.
  function sheetController(SP, opts) {
    var tier = opts.tierCfg;
    if (!tier || !tier.tiers || !tier.tiers.length) return null;
    var fillable = tier.tiers.filter(function (t) {
      return opts.maxQty == null || t.min <= opts.maxQty;
    });
    if (!fillable.length) return null;
    var sur = tier.unitSurchargeCents || 0;
    var floor = tier.minQty || 50;
    var overlay = null, elTitle, elIntro, elRows, elFoot;

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'saturn-tiers-overlay';
      overlay.id = 'saturn-tiers-modal';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'saturn-tiers-title');
      overlay.innerHTML =
        '<div class="saturn-tiers-modal">' +
          '<div class="sp-sheet__head"><h3 id="saturn-tiers-title" class="sp-sheet__title"></h3>' +
          '<button type="button" class="sp-sheet__x saturn-tiers-close" aria-label="Close">' + X_SVG + '</button></div>' +
          '<p class="sp-sheet__intro"></p>' +
          '<div data-tiers-list></div>' +
          '<div class="sp-sheet__foot" hidden></div>' +
        '</div>';
      document.body.appendChild(overlay);
      elTitle = overlay.querySelector('.sp-sheet__title');
      elIntro = overlay.querySelector('.sp-sheet__intro');
      elRows = overlay.querySelector('[data-tiers-list]');
      elFoot = overlay.querySelector('.sp-sheet__foot');
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || (e.target.closest && e.target.closest('.saturn-tiers-close'))) { close(); return; }
        var b = e.target.closest ? e.target.closest('button[data-sheet-act]') : null;
        if (!b || b.disabled) return;
        var act = b.getAttribute('data-sheet-act');
        var before = opts.getQty();
        if (act === 'set') {
          // A tier row sets the quantity and the sheet closes; the quantity
          // is retained (owner ruling 2026-07-31 — dismissal never reverts).
          opts.setQty(Math.max(parseInt(b.getAttribute('data-qty'), 10), floor));
          if (opts.onSelect) { try { opts.onSelect(before, opts.getQty()); } catch (err) { /* telemetry */ } }
          close();
        } else if (act === 'roundup') {
          opts.setQty(parseInt(b.getAttribute('data-qty'), 10));
          if (opts.onRoundUp) { try { opts.onRoundUp(before, opts.getQty()); } catch (err) { /* telemetry */ } }
          close();
        } else if (act === 'bulk') {
          // Door A: the page prices tiers at the floor itself (#66), so this
          // sets the quantity and the sheet re-renders as the bulk ladder —
          // it stays open (design: no re-open).
          opts.setQty(floor);
          if (opts.onSelect) { try { opts.onSelect(before, opts.getQty()); } catch (err) { /* telemetry */ } }
          render();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay.classList.contains('saturn-open')) close();
      });
    }

    function rowHtml(label, each, actionHtml) {
      return '<div class="sp-sheet__row"><span>' + label + '</span>' +
        '<span class="sp-sheet__each">' + each + '</span>' +
        '<span class="sp-sheet__go">' + (actionHtml || '') + '</span></div>';
    }

    function render() {
      var q = opts.getQty();
      var variant = opts.variant();
      var within = function (n) { return opts.maxQty == null || n <= opts.maxQty; };
      if (variant === 'bulk') {
        elTitle.textContent = 'Bulk pricing';
        elIntro.textContent = floorWord(floor) + '-card minimum. The per-card price steps down with quantity; choosing a row sets the quantity.';
        elRows.innerHTML = fillable.map(function (t, i) {
          var next = fillable[i + 1];
          var label = next ? t.min + '–' + (next.min - 1) + ' cards' : t.min + ' or more';
          var current = q >= t.min && (!next || q < next.min);
          var action = current
            ? '<button type="button" class="sp-link sp-sheet__go" disabled>Selected</button>'
            : '<button type="button" class="sp-link sp-sheet__go" data-sheet-act="set" data-qty="' + t.min + '">Set ' + t.min + '</button>';
          return rowHtml(label, SP.formatCents(t.unitCents + sur) + ' a card', action);
        }).join('');
        elFoot.hidden = true;
      } else {
        var block = opts.blockCfg;   // Door A's block config (single + set economics), or null
        elTitle.textContent = 'Volume pricing';
        elIntro.textContent = 'The per-card price steps down with quantity.';
        var rows = [];
        if (block && block.singleCents) {
          rows.push(rowHtml('1–' + (block.blockSize - 1) + ' cards', SP.formatCents(block.singleCents) + ' a card', ''));
          var setRate = SP.formatCents(block.blockPriceCents / block.blockSize) + ' a card';
          var setAct = '';
          if (opts.canRoundUp && q < block.blockSize && within(block.blockSize)) {
            setAct = '<button type="button" class="sp-link sp-sheet__go" data-sheet-act="roundup" data-qty="' + block.blockSize + '">Round up to ' + block.blockSize + '</button>';
          }
          rows.push(rowHtml(block.blockSize + ' cards', setRate, setAct));
        }
        var bulkAct = '';
        if (opts.bulkHref) {
          bulkAct = '<a class="sp-link sp-sheet__go" href="' + esc(opts.bulkHref) + '" data-sp-to="C" data-sp-src="sheet">Order in bulk</a>';
        } else if (opts.canOrderBulk && within(floor)) {
          bulkAct = '<button type="button" class="sp-link sp-sheet__go" data-sheet-act="bulk">Order in bulk</button>';
        }
        rows.push(rowHtml(floor + ' or more', SP.formatCents(fillable[0].unitCents + sur) + ' a card', bulkAct));
        elRows.innerHTML = rows.join('');
        elFoot.hidden = true;
      }
    }

    function open() { if (!overlay) build(); render(); overlay.classList.add('saturn-open'); if (opts.onOpenChange) opts.onOpenChange(true); }
    function close() { if (overlay) overlay.classList.remove('saturn-open'); if (opts.onOpenChange) opts.onOpenChange(false); }
    function isOpen() { return !!overlay && overlay.classList.contains('saturn-open'); }
    return {
      open: open,
      isOpen: isOpen,
      refresh: function () { if (isOpen()) render(); }
    };
  }

  // ── Personalize interstitial (ROADMAP #158, 2026-09-16) ─────────────────────
  // Sits between the "Personalize this card" pill on Doors A / B / C and the
  // composer (Door D): the design's front-and-interior mock from the 8/1
  // launch imagery, the story in one paragraph, the price at the
  // customer's CURRENT quantity (never below the floor; Door B's boxes become
  // cards), the minimum, and one primary action that continues. Copy is the
  // live Holiday Personalization collection page's, verbatim (design addendum
  // 2026-09-16, James green-lit 2026-09-15); every number is the P product's
  // own pricing_config through the engine, so the modal quotes what the
  // composer will charge. Dismissal — X, scrim, Escape — returns to the PDP
  // unchanged: no off-ramp link (owner). Same container as the volume sheet
  // (bottom sheet on phones, centred 480px on desktop). The pill stays a real
  // link to the composer, so a browser where this script never ran still
  // gets there; a modifier / middle click keeps the link's native behaviour.
  //
  // Telemetry rides the existing sidedoor vocabulary, no new event names: the
  // pill's own `sidedoor_click` (src: pill) now means "opened the interstitial",
  // and the primary action is a second cross-door anchor (src: bridge), so
  // pill − bridge is the drop-off the modal introduces. Both are counted by
  // label in the watch (a label is never folded — #160).
  function bridgeController(SP, opts) {
    var pf = opts.personalize;
    var dCfg = opts.doorCfg;
    if (!pf || !pf.url || !dCfg) return null;
    var floor = dCfg.minQty || 50;
    var tiers = dCfg.tiers || [];
    var sur = dCfg.unitSurchargeCents || 0;
    var fromCents = tiers.length ? tiers[0].unitCents + sur : null;
    var stepsAbove = tiers.length > 1 ? tiers[1].min - 1 : null;
    var overlay = null, elTotal, elMeta, lastFocus = null;
    // The DESIGN's name for the eyebrow (owner 2026-09-16): the page title is
    // "Design - Variant" on every holiday door (Greeted / Ungreeted / Holiday
    // Box — census of all four collections), so drop the last " - …" segment;
    // a bare title (Drift) passes through unchanged.
    var design = String(opts.title || '').replace(/\s+-\s+[^-]*$/, '').trim();

    // Cards the composer would be asked for: the page quantity in cards (a box
    // is `cardsPerUnit` of them), never below the Door D floor — below it the
    // modal quotes the minimum, not the customer's smaller number.
    function persQty() {
      var q = opts.getQty();
      if (opts.cardsPerUnit > 1) q = q * opts.cardsPerUnit;
      return Math.max(floor, q);
    }

    function build() {
      overlay = document.createElement('div');
      overlay.className = 'saturn-tiers-overlay';
      overlay.id = 'saturn-bridge-modal';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'saturn-bridge-title');
      var fig = '';
      if (pf.image && pf.image.src) {
        var dims = (pf.image.width && pf.image.height)
          ? ' width="' + parseInt(pf.image.width, 10) + '" height="' + parseInt(pf.image.height, 10) + '"' : '';
        fig = '<figure class="sp-bridge__fig"><img src="' + esc(pf.image.src) + '" alt="' + esc(pf.image.alt || '') + '"' + dims + ' decoding="async"></figure>';
      }
      var note = 'Personalized cards start at ' + (fromCents != null ? SP.formatCents(fromCents) + ' each' : 'the per-card rate') +
        (stepsAbove ? '; the per-card rate steps down above ' + stepsAbove : '') +
        '. ' + floor + ' cards minimum order, per design.';
      overlay.innerHTML =
        '<div class="saturn-tiers-modal sp-bridge">' +
          '<div class="sp-bridge__body">' +
          '<div class="sp-bridge__head"><div>' +
            '<p class="sp-eyebrow">Personalized · ' + esc(design) + '</p>' +
            '<h3 id="saturn-bridge-title" class="sp-bridge__title">The inside of the card is <em>yours</em></h3>' +
          '</div>' +
          '<button type="button" class="sp-sheet__x saturn-bridge-close" aria-label="Close">' + X_SVG + '</button></div>' +
          fig +
          '<p class="sp-bridge__desc">We set your words in metal type and press them into heavy paper in Kent, Connecticut — and we leave you room to add a note by hand for the people who’d want one.</p>' +
          '</div>' +
          '<div class="sp-bridge__foot">' +
          '<div class="sp-bridge__price"><span class="sp-bridge__total" data-bridge-total></span><span class="sp-bridge__meta" data-bridge-meta></span></div>' +
          '<p class="sp-bridge__note">' + note + '</p>' +
          '<a class="sp-bridge__go" href="' + esc(pf.url) + '" data-sp-to="D" data-sp-src="bridge">Personalize this card</a>' +
          '</div>' +
        '</div>';
      document.body.appendChild(overlay);
      elTotal = overlay.querySelector('[data-bridge-total]');
      elMeta = overlay.querySelector('[data-bridge-meta]');
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || (e.target.closest && e.target.closest('.saturn-bridge-close'))) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) close();
      });
    }

    function render() {
      var q = persQty();
      var r = SP.computePricing(dCfg, q);
      if (r && r.valid) {
        elTotal.textContent = SP.formatCents(r.subtotalCents);
        elMeta.textContent = q + ' cards · ' + SP.formatCents(r.unitEffectiveCents) + ' a card';
      } else {
        elTotal.textContent = '';
        elMeta.textContent = q + ' cards';
      }
    }

    function open(opener) {
      if (!overlay) build();
      render();
      lastFocus = opener || document.activeElement;
      overlay.classList.add('saturn-open');
      var x = overlay.querySelector('.saturn-bridge-close');
      if (x && x.focus) { try { x.focus(); } catch (e) { /* focus is a courtesy */ } }
      if (opts.onOpenChange) opts.onOpenChange(true);
    }
    function close() {
      if (!overlay || !isOpen()) return;
      overlay.classList.remove('saturn-open');
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) { /* courtesy */ } }
      lastFocus = null;
      if (opts.onOpenChange) opts.onOpenChange(false);
    }
    function isOpen() { return !!overlay && overlay.classList.contains('saturn-open'); }
    return {
      open: open,
      close: close,
      isOpen: isOpen,
      refresh: function () { if (isOpen()) render(); }
    };
  }

  // ── Two-path sheet adapter (ROADMAP #223, 2026-10-05) ──────────────────────
  // When saturn-personalize-sheet.js is on the page (the `custom.set_for_you`
  // switch is open — saturn-pricing-data.liquid decides), the pill opens the
  // shared two-path sheet instead of the #158 one-path bridge. Same contract as
  // bridgeController (open / close / isOpen / refresh), same numbers: the self
  // path quotes the customer's CURRENT quantity in cards, never below the
  // floor, through the engine on the P product's own config. The continue link
  // keeps data-sp-to="D" data-sp-src="bridge", so the #158 pill − bridge read
  // stays comparable across the change.
  function sheetBridge(SP, opts) {
    var PS = window.SaturnPersonalizeSheet;
    var pf = opts.personalize;
    var dCfg = opts.doorCfg;
    if (!PS || !pf || !pf.url || !dCfg) return null;
    var floor = dCfg.minQty || 50;
    var tiers = dCfg.tiers || [];
    var sur = dCfg.unitSurchargeCents || 0;
    var design = String(opts.title || '').replace(/\s+-\s+[^-]*$/, '').trim();
    var handle = pf.handle || ((pf.url.match(/\/products\/([^\/?#]+)/) || [])[1]) || null;
    function persQty() {
      var q = opts.getQty();
      if (opts.cardsPerUnit > 1) q = q * opts.cardsPerUnit;
      return Math.max(floor, q);
    }
    return {
      open: function (opener) {
        PS.open({
          source: 'pdp',
          handle: handle,
          design: design,
          url: pf.url,
          image: pf.image || null,
          thumb: pf.thumb || null,
          goAttrs: ' data-sp-to="D" data-sp-src="bridge"',
          price: {
            floor: floor,
            fromCents: tiers.length ? tiers[0].unitCents + sur : null,
            quote: function () {
              var q = persQty();
              var r = SP.computePricing(dCfg, q);
              return r && r.valid ? { qty: q, totalCents: r.subtotalCents, unitCents: r.unitEffectiveCents } : null;
            }
          },
          opener: opener,
          onOpenChange: opts.onOpenChange
        });
      },
      close: function () { PS.close(); },
      isOpen: function () { return PS.isOpen(); },
      refresh: function () { PS.refresh(); }
    };
  }

  function init() {
    var SP = window.SaturnPricing;
    if (!SP || !SP.computePricing) { warn('saturn-pricing.js not loaded before this script'); return; }

    var config = readConfig();
    if (!config || !config.doors) { warn('no pricing config on this product; skipping'); return; }

    // Pick the theme adapter the island opted into (default Dawn), then let it
    // resolve the theme elements — by id on Eurus, by scoped selector on Dawn.
    var islandEl = document.getElementById('saturn-pricing-data');
    if (islandEl && islandEl.getAttribute('data-adapter') === 'eurus') {
      ADAPTER = eurusAdapter(islandEl.getAttribute('data-section-id'));
    }
    var els = ADAPTER.resolve();
    var form = els.form;
    var qtyInput = els.qtyInput;
    var addBtn = els.addBtn;
    var anchor = els.anchor;
    var afterAnchor = els.afterAnchor || anchor;
    var payWrap = els.payWrap;
    if (!form || !qtyInput || !anchor) { warn('expected theme elements not found; check adapter selectors'); return; }

    // Holiday PDPs only (we're past the pricing_config check, so Everyday cards
    // never reach here): our buy box owns the price, so hide the theme's native
    // price line below the title to avoid a stale, door-blind number.
    if (ADAPTER.nativePriceSelector) {
      var nativePrice = $(ADAPTER.nativePriceSelector);
      if (nativePrice) {
        var priceWrap = ADAPTER.nativePriceClosest && nativePrice.closest(ADAPTER.nativePriceClosest);
        (priceWrap || nativePrice).style.display = 'none';
      }
    }

    // Mark the page as a Holiday PDP so the scoped CSS applies (and nothing
    // leaks onto Everyday cards, which never reach this point).
    document.body.classList.add('saturn-holiday-pdp');

    // Cross-door data: the design's siblings (Individual / Box / Bulk /
    // Personalized) with their availability, this product's own door
    // reachability, and the context-switch URL to the Bulk view of this product.
    var family = readFamily();
    var doorsAvailable = readDoors();
    // Stock cap for the ADVERTISING layer (owner bug 2026-08-01: 1226G at 116 in
    // stock still offered and priced 200-card quantities). Number only when the
    // variant is tracked + deny (island emits null for untracked / continue-
    // selling = uncapped). Caps what we OFFER — the qty input's max, every
    // programmatic qty set, tier rows, round-up targets. Never touches what a
    // given quantity is PRICED at (#66: display must equal charge everywhere).
    var availableQty = (typeof doorsAvailable.availableQty === 'number') ? doorsAvailable.availableQty : null;
    if (availableQty != null) {
      var themeMax = parseInt(qtyInput.max, 10);
      if (!themeMax || availableQty < themeMax) qtyInput.max = availableQty;
    }
    var withinStock = function (q) { return availableQty == null || q <= availableQty; };
    var handleMatch = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    var productUrl = handleMatch ? '/products/' + handleMatch[1] : window.location.pathname;
    var bulkUrl = (handleMatch && ADAPTER.bulkCollection)
      ? '/collections/' + ADAPTER.bulkCollection + '/products/' + handleMatch[1] : null;

    // "Get the same price without the box" destination (James walkthrough
    // 2026-07-24): the Individual sibling when card_design wires one. The claim
    // is exact by construction: a block of 8 singles prices identically to the
    // Box ($35), just without the box.
    var sansBoxUrl = (family && family.individual && family.individual.url) ? family.individual.url : null;

    var activeDoor = resolveDoor(config);
    var pageCfg = config.doors[activeDoor];
    var isBlockPage = pageCfg.model === 'block_pricing';
    var isBulkPage = isBulkDoor(pageCfg);
    var isBoxPage = pageCfg.model === 'box_multiple';
    // The block (set-of-8) economics — Door A's own config, or on Door B the
    // Individual sibling's via the bulk family entry (same product as Door A),
    // so the box page's sheet can name the single and set rungs.
    function findBlockCfg(c) {
      if (!c || !c.doors) return null;
      for (var dk in c.doors) {
        if (Object.prototype.hasOwnProperty.call(c.doors, dk) && c.doors[dk].model === 'block_pricing') return c.doors[dk];
      }
      return null;
    }
    var blockCfg = findBlockCfg(config) || (family && family.bulk ? findBlockCfg(family.bulk.config) : null);
    // The tier ladder: this product's own (Doors A/C share a config), else the
    // Bulk sibling's (Door B, via the family island).
    var tierCfg = findTierCfg(config) || (family && family.bulk && family.bulk.config ? findTierCfg(family.bulk.config) : null);
    var tierFloor = tierCfg ? (tierCfg.minQty || 50) : 50;
    var tierSur = tierCfg ? (tierCfg.unitSurchargeCents || 0) : 0;
    var tierBest = (tierCfg && tierCfg.tiers && tierCfg.tiers.length) ? tierCfg.tiers[0].unitCents + tierSur : null;
    var blockSize = (blockCfg && blockCfg.blockSize) || (isBoxPage && pageCfg.blockSize) || 8;
    // Whether this page may TALK about volume pricing (never whether it charges
    // it): Door A only while stock can fill a floor order (#66's volumeOfferable,
    // inventory > 50); Door C always; Door B only when the design's bulk door
    // is open (family island).
    var volumeTalk = isBulkPage || (isBlockPage && !!doorsAvailable.volumeOfferable) ||
      (isBoxPage && !!(family && family.bulk && family.bulk.open));

    // ── Telemetry (ROADMAP #137, 2026-09-11) ─────────────────────────────────
    // Rides the transport saturn-resume-nudge.liquid exposes (one consent
    // rule, one visitor id, one build stamp for every host); absent it, every
    // call is a no-op. Every event carries build · door (the PAGE door) ·
    // vw · ptr, then its own keys — the endpoint keeps 8 meta keys at most.
    // Labels and numbers only; never text. Telemetry never breaks the page.
    var BEACON = window.SaturnBeacon || null;
    var pageHandle = handleMatch ? handleMatch[1] : null;
    function deviceMeta() {
      var m = {};
      try {
        var w = window.innerWidth || 0;
        m.vw = w < 600 ? 'sm' : w < 900 ? 'md' : 'lg';
        m.ptr = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ? 'coarse' : 'fine';
      } catch (e) { /* telemetry never breaks the page */ }
      return m;
    }
    function tele(name, meta) {
      if (!BEACON || !BEACON.emit) return;
      try {
        BEACON.emit(name, Object.assign({ build: BEACON.build || 'unstamped', door: activeDoor }, deviceMeta(), meta || {}), pageHandle);
      } catch (e) { /* telemetry never breaks the page */ }
    }
    // Quantity band for the add-to-cart press — the same edges the door read
    // uses on the ORDER side (lib/reports/doorRead.ts BAND_SQL).
    function qtyBand(doorCfg, qty) {
      if (doorCfg && doorCfg.model === 'box_multiple') return qty <= 1 ? '1 box' : qty <= 2 ? '2 boxes' : '3+ boxes';
      if (qty < 8) return '1-7';
      if (qty < 16) return '8-15';
      if (qty < 50) return '16-49';
      var tiers = (tierCfg && tierCfg.tiers) || [];
      for (var i = 0; i < tiers.length; i++) {
        var next = tiers[i + 1];
        if (qty >= tiers[i].min && (!next || qty < next.min)) {
          return next ? tiers[i].min + '-' + (next.min - 1) : tiers[i].min + '+';
        }
      }
      return '50+';
    }

    // Single write path for programmatic quantity sets. The value write alone
    // isn't enough on Eurus: the qty input is Alpine x-model-bound, so without
    // an 'input' event Alpine's state goes stale and the next +/- click jumps
    // from the OLD quantity. 'change' stays for Dawn's listeners. Synthetic
    // events are !isTrusted, so the ?qty= preset stand-down (trusted-only) is
    // unaffected. No-op sets return early — a dispatch can round-trip through
    // theme quantity events back into ensureMin/applyPresetQty, and the early
    // return is what terminates that cycle.
    function setQtyValue(n) {
      n = parseInt(n, 10) || 0;
      if (availableQty != null && n > availableQty) n = availableQty;
      var s = String(n);
      if (qtyInput.value === s) return;
      qtyInput.value = s;
      qtyInput.setAttribute('value', s); // some themes re-read the attribute
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function getQty() { return parseInt(qtyInput.value, 10) || 0; }
    function ensureMin() {
      var min = pageCfg.minQty || 1;
      if (getQty() < min) setQtyValue(min);
    }

    injectStyles();

    // ── Eyebrow, before the theme's title ────────────────────────────────────
    var titleEl = ADAPTER.titleSelector ? $(ADAPTER.titleSelector) : null;
    // The heading's own text (Dawn's title block nests a duplicate h2 for its
    // hover state; Eurus's selector is the h1 itself).
    var headingEl = titleEl ? (titleEl.matches('h1,h2,h3') ? titleEl : (titleEl.querySelector('h1,h2,h3') || titleEl)) : null;
    var titleText = headingEl ? (headingEl.textContent || '').trim() : (document.title || '');
    if (titleEl) {
      var eyebrow = document.createElement('div');
      eyebrow.className = 'sp-eyebrow';
      eyebrow.textContent = isBoxPage ? 'Holiday boxed sets · Letterpress'
        : isBulkPage ? 'Holiday bulk · ' + tierFloor + '-card minimum'
        : 'Holiday cards · Letterpress';
      var titleHost = (ADAPTER.titleClosest && titleEl.closest(ADAPTER.titleClosest)) || titleEl;
      titleHost.insertAdjacentElement('beforebegin', eyebrow);
    }

    // ── The panel: picker · bridge note · price row ──────────────────────────
    // Picker segments are LINKS between the design's door pages (Door A and C
    // are the same product in two collection contexts; Door B is the box
    // sibling). A segment renders only for a door that is open right now:
    // Box while the box product is available, Bulk while the design sits in
    // the Holiday Bulk collection (the availability oracle). The current door
    // is a non-link segment.
    function pickerSegments() {
      var segs = [];
      var aUrl = isBoxPage
        ? ((family && family.individual && family.individual.available) ? family.individual.url : null)
        : productUrl;
      if (aUrl) segs.push({ door: 'A', label: 'Single', href: aUrl, current: isBlockPage });
      var bUrl = isBoxPage ? productUrl
        : ((family && family.box && family.box.available) ? family.box.url : null);
      if (bUrl) segs.push({ door: 'B', label: 'Box of ' + blockSize, href: bUrl, current: isBoxPage });
      var cUrl = isBulkPage ? bulkUrl
        : isBoxPage ? ((family && family.bulk && family.bulk.open) ? family.bulk.url : null)
        : ((doorsAvailable.C && bulkUrl) ? bulkUrl : null);
      if (cUrl) segs.push({ door: 'C', label: 'Bulk ' + tierFloor + '+', href: cUrl, current: isBulkPage });
      return segs;
    }
    var panel = document.createElement('div');
    panel.className = 'saturn-pricing';
    panel.id = 'saturn-pricing';
    var segs = pickerSegments();
    panel.innerHTML =
      (segs.length > 1
        ? '<nav class="sp-picker" aria-label="Format">' + segs.map(function (sg) {
            return sg.current
              ? '<span class="sp-picker__seg is-current" aria-current="page">' + sg.label + '</span>'
              : '<a class="sp-picker__seg" href="' + esc(sg.href) + '" data-sp-to="' + sg.door + '" data-sp-src="picker">' + sg.label + '</a>';
          }).join('') + '</nav>'
        : '') +
      '<p class="sp-note sp-fade" data-bridge hidden></p>' +
      '<div class="sp-price" data-pricerow><span class="sp-price__total" data-total></span><span class="sp-price__meta" data-meta></span></div>' +
      '<div class="saturn-pricing__error" data-error hidden></div>';
    anchor.insertAdjacentElement(ADAPTER.injectPosition, panel);
    var elBridge = panel.querySelector('[data-bridge]');
    var elPriceRow = panel.querySelector('[data-pricerow]');
    var elTotal = panel.querySelector('[data-total]');
    var elMeta = panel.querySelector('[data-meta]');
    var elError = panel.querySelector('[data-error]');

    // ── The after-container: payment fold · round-up · pointer · bridge · pill ─
    var after = document.createElement('div');
    after.className = 'saturn-pricing saturn-pricing--more';
    after.id = 'saturn-pricing-more';
    after.innerHTML =
      '<p class="sp-pay" data-pay hidden><button type="button" class="sp-pay__toggle" aria-expanded="false">More payment options' + CHEV_SVG + '</button></p>' +
      '<div class="sp-pills" data-pills>' +
        '<span data-roundup hidden></span>' +
        '<span class="sp-pills__sep" data-sep hidden aria-hidden="true">|</span>' +
        '<span data-personalize hidden></span>' +
        '<span class="sp-pill__price" data-pprice hidden></span>' +
      '</div>' +
      '<p class="sp-hint" data-hint hidden></p>' +
      '<p class="sp-hint sp-hint--bridge" data-boxbridge hidden></p>';
    afterAnchor.insertAdjacentElement('afterend', after);
    var elPay = after.querySelector('[data-pay]');
    var elRoundup = after.querySelector('[data-roundup]');
    var elSep = after.querySelector('[data-sep]');
    var elHint = after.querySelector('[data-hint]');
    var elBoxBridge = after.querySelector('[data-boxbridge]');
    var elPersonalize = after.querySelector('[data-personalize]');
    var elPPrice = after.querySelector('[data-pprice]');
    // The pipe between the two pills belongs only to a shared line: when the
    // column is too narrow and the pills stack, it goes.
    function layoutPills() {
      var a = elRoundup.hidden ? null : elRoundup.firstElementChild;
      var b = elPersonalize.hidden ? null : elPersonalize.firstElementChild;
      if (!a || !b) { elSep.hidden = true; return; }
      elSep.hidden = false;
      if (Math.abs(a.getBoundingClientRect().top - b.getBoundingClientRect().top) > 4) elSep.hidden = true;
    }
    window.addEventListener('resize', layoutPills);

    // Payment disclosure: the theme's dynamic-checkout container (Buy with Shop
    // + "More payment options", one payment_button unit — which wallet shows is
    // Shopify's call) starts hidden and is revealed in place by the toggle. On
    // Eurus the container lazy-loads on intersection, so a hidden container
    // never loads the wallet until asked for.
    if (payWrap) {
      payWrap.style.display = 'none';
      elPay.hidden = false;
      elPay.querySelector('button').addEventListener('click', function () {
        payWrap.style.display = '';
        payWrap.classList.add('sp-fade');
        elPay.hidden = true;
      });
    }

    // ── The sheet ────────────────────────────────────────────────────────────
    var presetQty = 0;
    var bridge = null;   // the Personalize interstitial (#158), wired below the pill
    var sheet = sheetController(SP, {
      tierCfg: tierCfg,
      blockCfg: blockCfg,
      maxQty: availableQty,
      getQty: getQty,
      setQty: function (n) { presetQty = 0; setQtyValue(n); },
      variant: function () { return (isBulkPage || (isBlockPage && getQty() >= tierFloor)) ? 'bulk' : 'single'; },
      canRoundUp: isBlockPage,
      canOrderBulk: isBlockPage && !!doorsAvailable.volumeOfferable,
      bulkHref: isBoxPage && family && family.bulk && family.bulk.open ? family.bulk.url + '?qty=' + tierFloor : null,
      onSelect: function (from, to) { tele('ladder_tier_selected', { from: from, to: to }); },
      onRoundUp: function (from, to) { tele('roundup_click', { from: from, to: to, kind: 'block' }); },
      onOpenChange: function () { updateSticky(); }
    });
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('.js-saturn-tiers') : null;
      if (t && sheet) {
        e.preventDefault();
        sheet.open();
        tele('ladder_opened', { qty: getQty() }); // #137
      }
    });

    // ── Sticky Add-to-cart bar ───────────────────────────────────────────────
    // Shows once the price row has scrolled under the (sticky) header, hides
    // while the sheet is open, and seats itself ABOVE any bottom-fixed overlay
    // the page carries (the email tool's teaser holds the bottom 50px of a
    // phone screen from the first second) — the offset reads the overlay's
    // presence rather than assuming it. Its button presses the real Add to
    // cart, so the form, the _door stamp and the beacon all see one press.
    var sticky = document.createElement('div');
    sticky.className = 'sp-sticky';
    sticky.hidden = true;
    sticky.innerHTML =
      '<div class="sp-sticky__text"><div class="sp-sticky__title">' + esc(titleText) + '</div><div class="sp-sticky__meta"></div></div>' +
      '<button type="button" class="sp-sticky__atc">Add to cart</button>';
    document.body.appendChild(sticky);
    var elStickyMeta = sticky.querySelector('.sp-sticky__meta');
    var elStickyBtn = sticky.querySelector('.sp-sticky__atc');
    elStickyBtn.addEventListener('click', function () {
      if (addBtn && !addBtn.disabled) addBtn.click();
    });
    function bottomOverlayHeight() {
      var vh = window.innerHeight || 0, best = 0;
      try {
        // Candidates: body's children and grandchildren (overlay hosts often
        // mount a static wrapper with the fixed bar inside), plus anything the
        // email tool names a teaser — on live (2026-09-15) the teaser sits
        // three levels down, where the first pass never looked and the bar
        // rendered underneath it.
        var cands = [];
        var kids = document.body.children;
        for (var i = 0; i < kids.length; i++) {
          cands.push(kids[i]);
          var gk = kids[i].children;
          for (var j = 0; j < gk.length && j < 6; j++) cands.push(gk[j]);
        }
        var named = document.querySelectorAll('[class*="kl-teaser"],[class*="klaviyo-teaser"],[class*="teaser"]');
        for (var n = 0; n < named.length; n++) cands.push(named[n]);
        for (var c = 0; c < cands.length; c++) {
          var cand = cands[c];
          if (cand === sticky || cand.id === 'saturn-tiers-modal' || cand.contains(sticky)) continue;
          var cs = window.getComputedStyle(cand);
          if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
          var r = cand.getBoundingClientRect();
          if (r.height < 24 || r.height > 96 || r.width < (window.innerWidth || 0) * 0.5) continue;
          if (Math.abs(r.bottom - vh) > 3) continue;
          best = Math.max(best, r.height);
        }
      } catch (e) { /* layout probe never breaks the page */ }
      return best;
    }
    var stickyTicking = false;
    function updateSticky() {
      if (stickyTicking) return;
      stickyTicking = true;
      window.requestAnimationFrame(function () {
        stickyTicking = false;
        var show = false;
        try {
          var header = ADAPTER.headerSelector ? $(ADAPTER.headerSelector) : null;
          var hb = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
          show = elPriceRow.getBoundingClientRect().bottom < hb && !(sheet && sheet.isOpen()) && !(bridge && bridge.isOpen());
        } catch (e) { show = false; }
        if (show) {
          sticky.style.bottom = bottomOverlayHeight() + 'px';
          sticky.hidden = false;
        } else {
          sticky.hidden = true;
        }
      });
    }
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);

    // ── Personalize pill (static: link + gate; the price line is per render) ─
    var pf = family && family.personalize;
    var pDoor = (pf && pf.available && pf.url && pf.config && pf.config.doors)
      ? (pf.config.doors[pf.config.defaultDoor] || pf.config.doors.D) : null;
    if (pf && pf.available && pf.url) {
      elPersonalize.hidden = false;
      elPersonalize.innerHTML =
        '<a class="sp-pill__btn saturn-pricing__sidedoor-cta" href="' + esc(pf.url) + '" data-sp-to="D" data-sp-src="pill">' + PEN_SVG + 'Personalize this card</a>';
    }
    // The interstitial the pill opens (#158). Built lazily on first press;
    // null when the family carries no Door D config, in which case the pill
    // is the plain link it always was.
    var bridgeOpts = {
      personalize: pf,
      doorCfg: pDoor,
      title: titleText,
      getQty: getQty,
      cardsPerUnit: isBoxPage ? ((pageCfg.blockSize) || blockSize) : 1,
      onOpenChange: function () { updateSticky(); }
    };
    bridge = (pf && pf.available && pf.url)
      ? (sheetBridge(SP, bridgeOpts) || bridgeController(SP, bridgeOpts)) : null;
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('.saturn-pricing__sidedoor-cta') : null;
      if (!a || !bridge) return;
      // Modifier / middle clicks keep the link's own behaviour (a new tab).
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      bridge.open(a);
    });

    function render() {
      var qty = getQty();
      // Page IDENTITY (eyebrow, picker, qty floor, the stamped _door property)
      // follows the door the customer arrived through; only the MONEY follows
      // the #66 flip: the CHARGED door is structure + qty only — resolved by
      // the canonical rule in saturn-pricing.js, which the parity test guards
      // against the Discount Function's mirror. Not inventory-gated.
      var doorCfg = SP.resolveBillingDoor(config, pageCfg, qty);
      var r = SP.computePricing(doorCfg, qty);
      var bulkNow = isBulkDoor(doorCfg);

      // Page door's floor, NOT the billing door's — otherwise flipping to the
      // tier config at 50 would raise the input's min to 50 and trap the
      // customer above the block range they came in for.
      qtyInput.setAttribute('min', pageCfg.minQty || 1);

      if (!r.valid) {
        elTotal.textContent = ''; elMeta.textContent = '';
        elError.hidden = false; elError.textContent = r.validationMessage;
        elRoundup.hidden = true; elHint.hidden = true; elBoxBridge.hidden = true; elBridge.hidden = true;
        elPPrice.hidden = true; layoutPills();
        if (addBtn) addBtn.setAttribute('disabled', 'disabled');
        elStickyBtn.setAttribute('disabled', 'disabled');
        elStickyMeta.textContent = r.validationMessage || '';
        setDoorProperty(form, activeDoor);
        updateSticky();
        return;
      }

      if (addBtn) addBtn.removeAttribute('disabled');
      elStickyBtn.removeAttribute('disabled');
      elError.hidden = true;

      // Price row: total · "N cards · $X each" (boxes: "N boxes of 8 · $X a card").
      var total = SP.formatCents(r.subtotalCents);
      var unit = SP.formatCents(r.unitEffectiveCents);
      elTotal.textContent = total;
      elMeta.textContent = (doorCfg.model === 'box_multiple')
        ? plural(qty, 'box', 'boxes') + ' of ' + (doorCfg.blockSize || blockSize) + ' · ' + unit + ' a card'
        : plural(qty, 'card', 'cards') + ' · ' + unit + ' each';
      elStickyMeta.textContent = (doorCfg.model === 'box_multiple' ? plural(qty, 'box', 'boxes') : plural(qty, 'card', 'cards')) + ' · ' + total;

      // Bridge note: arrived on Door A over the Box→A bridge with the box's
      // card count. Stands down with the preset (first real touch).
      var bridged = isBlockPage && presetQty > 0 && !!(family && family.box) &&
        presetQty % blockSize === 0 && presetQty < tierFloor && qty === presetQty;
      if (bridged) {
        elBridge.hidden = false;
        elBridge.textContent = 'Same ' + plural(qty, 'card', 'cards') + ', same ' + total + ' — just without the box.';
      } else { elBridge.hidden = true; }

      // Round-up pill (Door A, below the floor): "Round up to N — $T · $R a
      // card", the rate inside the pill (owner 2026-09-15). Target = the next
      // multiple of the set size up to the last one under the floor; from
      // there it rounds to the floor itself, which is both cheaper and fewer
      // cards than the next set (#66 tier-crossing). Targets respect stock.
      elRoundup.hidden = true; elRoundup.innerHTML = '';
      function roundUpPill(kind, target, totalCents, rateCents) {
        return '<button type="button" class="sp-pill__btn saturn-pricing__roundup" data-kind="' + kind + '" data-qty="' + target + '">' +
          PLUS_SVG + 'Round up to ' + target + ' — ' + SP.formatCents(totalCents) + ' · ' + SP.formatCents(rateCents) + ' a card</button>';
      }
      if (isBlockPage && !bulkNow && blockCfg) {
        var bs = blockCfg.blockSize;
        var capQty = Math.floor((tierFloor - 1) / bs) * bs;
        var next = qty % bs === 0 ? qty + bs : Math.ceil(qty / bs) * bs;
        if (next <= capQty && withinStock(next)) {
          var atNext = SP.computePricing(doorCfg, next);
          if (atNext && atNext.valid) {
            elRoundup.hidden = false;
            elRoundup.innerHTML = roundUpPill('block', next, atNext.subtotalCents, blockCfg.blockPriceCents / bs);
          }
        } else if (tierCfg && doorsAvailable.volumeOfferable && qty < tierFloor && withinStock(tierFloor)) {
          var atFloor = SP.computePricing(tierCfg, tierFloor);
          if (atFloor && atFloor.valid) {
            elRoundup.hidden = false;
            elRoundup.innerHTML = roundUpPill('tier_cross', tierFloor, atFloor.subtotalCents, atFloor.unitEffectiveCents);
          }
        }
      }

      // Volume pointer: one quiet line; "See volume pricing" opens the sheet.
      var link = ' <button type="button" class="sp-link js-saturn-tiers">See volume pricing</button>';
      if (sheet && volumeTalk && tierBest != null) {
        elHint.hidden = false;
        if (isBulkPage) {
          elHint.innerHTML = floorWord(tierFloor) + '-card minimum · ' + unit + ' a card at this quantity.' + link;
        } else if (bulkNow) {
          elHint.innerHTML = 'Bulk pricing applied · ' + unit + ' a card at this quantity.' + link;
        } else {
          elHint.innerHTML = floorWord(tierFloor) + ' or more from ' + SP.formatCents(tierBest) + ' a card.' + link;
        }
      } else { elHint.hidden = true; elHint.innerHTML = ''; }

      // Door B's bridge to A: a quiet action, never a pushy nudge (James
      // 2026-07-24). Carries the box count as cards so the A page opens at the
      // set price with the bridge note above it.
      if (isBoxPage && sansBoxUrl) {
        var carryCards = (r.quantity || 1) * (doorCfg.blockSize || blockSize);
        elBoxBridge.hidden = false;
        elBoxBridge.innerHTML = (doorCfg.blockSize || blockSize) + ' cards, boxed. ' +
          '<a class="sp-link saturn-pricing__sansbox" href="' + esc(sansBoxUrl + '?qty=' + carryCards) + '" data-sp-to="A" data-sp-src="panel">Get the same price without the box</a>';
      } else { elBoxBridge.hidden = true; }

      // "Make it yours": at the floor on A / C, the Door D price at this
      // quantity under the pill (the sibling's own config, live).
      var pr = (pDoor && bulkNow && !elPersonalize.hidden) ? SP.computePricing(pDoor, qty) : null;
      if (pr && pr.valid) {
        elPPrice.hidden = false;
        elPPrice.textContent = 'Personalized at ' + qty + ': ' + SP.formatCents(pr.subtotalCents) + ' · ' + SP.formatCents(pr.unitEffectiveCents) + ' a card';
      } else { elPPrice.hidden = true; }
      layoutPills();

      if (sheet) sheet.refresh();
      if (bridge) bridge.refresh();
      setDoorProperty(form, activeDoor);
      updateSticky();
    }

    // ?qty= preset (the Box→A "Get the same price without the box" bridge
    // carries the shopper's card count; walkthrough bug 2026-07-27). One-shot
    // but Dawn-proof: setQuantityBoundries hard-resets the input to 1 after a
    // late network fetch, and ensureMin only re-raises to the door MINIMUM (1
    // on the block door) — so the preset re-asserts on the same schedule as
    // ensureMin until the first real user touch, then stands down for good.
    (function () {
      var m = /[?&]qty=(\d+)/.exec(location.search);
      if (m) presetQty = parseInt(m[1], 10) || 0;
    })();
    function applyPresetQty() {
      if (!presetQty) return;
      var max = parseInt(qtyInput.max, 10);
      setQtyValue(max && presetQty > max ? max : presetQty);
    }
    qtyInput.addEventListener('input', function (e) { if (e.isTrusted) presetQty = 0; });

    // A typed quantity above stock snaps back when committed (blur/Enter) —
    // Eurus's Alpine blur check only validates B2B quantity-rule maxes, not the
    // stock cap we set above. setQtyValue's no-op guard ends the event cycle.
    if (availableQty != null) {
      qtyInput.addEventListener('change', function () {
        if (getQty() > availableQty) setQtyValue(availableQty);
      });
    }

    qtyInput.addEventListener('input', render);
    qtyInput.addEventListener('change', render);

    // Round-up row (delegated, since it re-renders each time): jump the
    // quantity to the target, sync the theme's qty input, and re-price.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.saturn-pricing__roundup') : null;
      if (!btn) return;
      // Stand the ?qty= preset down: without this, a later Dawn quantity-update
      // re-assert (applyPresetQty rides it) would stomp the round-up back to
      // the bridge's carried quantity.
      presetQty = 0;
      // #137: the one-tap round-up, from → to, and which nudge produced it
      // (complete-the-set vs the tier-crossing walk to the floor).
      tele('roundup_click', {
        from: getQty(),
        to: parseInt(btn.getAttribute('data-qty'), 10) || 0,
        kind: btn.getAttribute('data-kind') || 'block'
      });
      setQtyValue(btn.getAttribute('data-qty'));
      render();
    });

    form.addEventListener('submit', function (e) {
      if (getQty() < (pageCfg.minQty || 1)) { e.preventDefault(); render(); }
    });

    // ── #137 emitters: pageview, side doors, add-to-cart ─────────────────────
    if (BEACON) {
      // A tester's browser declares itself on this host too (?sp_internal=1 —
      // ROADMAP #122); the watch excludes the visitor id everywhere it appears.
      try { if (BEACON.internal && BEACON.internal()) BEACON.emit('internal_declared', null, pageHandle); } catch (e) {}
      // The pageview. `preset` > 0 = arrived over the Box→A bridge with the
      // shopper's card count. First touch (src/med/cmp or ref) rides last:
      // build · door · vw · ptr · preset + up to three attribution keys = 8.
      var ft = {};
      try { ft = (BEACON.firstTouch && BEACON.firstTouch()) || {}; } catch (e) {}
      tele('door_pdp_loaded', Object.assign({ preset: presetQty || 0 }, ft));
      // Side doors, delegated. Every cross-door anchor this script renders
      // carries data-sp-to (destination door) and data-sp-src (where the link
      // sat: picker · pill · panel · sheet · bridge); a family-row tile (legacy) is
      // classified by its href. A link to the door already on screen is not a
      // crossing.
      document.addEventListener('click', function (e) {
        try {
          var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
          if (!a) return;
          var to = a.getAttribute('data-sp-to'), src = a.getAttribute('data-sp-src');
          if (!to && a.closest('.saturn-family-tile')) {
            src = 'family';
            var m = (a.getAttribute('href') || '').match(/\/collections\/([^\/?#]+)\//);
            to = (m && ADAPTER.collectionDoorMap[m[1]]) || '?';
          }
          if (!to || to === activeDoor) return;
          tele('sidedoor_click', { to: to, src: src || 'panel' }); // sendBeacon survives the navigation
        } catch (err) { /* telemetry never breaks the page */ }
      }, true);
      // Add to cart PRESSED at a valid quantity — the terminal step this host
      // can see (the cart guard and Shopify decide what actually lands). Heard
      // on the form's submit AND the button's click because Eurus adds via
      // its own handler; a press within 800 ms of the last is the same press.
      var lastAtcAt = 0;
      function atcPressed() {
        var qty = getQty();
        if (qty < (pageCfg.minQty || 1)) return;
        var now = Date.now();
        if (now - lastAtcAt < 800) return;
        lastAtcAt = now;
        tele('door_atc_click', { qty: qty, band: qtyBand(pageCfg, qty) });
      }
      form.addEventListener('submit', atcPressed, true);
      if (addBtn) addBtn.addEventListener('click', atcPressed, true);
    }

    ensureMin();   // set the floor on first load
    applyPresetQty();
    render();

    // Dawn's <quantity-input> can re-assert its own default of 1 *after* this init,
    // which on a 50-min door leaves the wrong qty plus a spurious "minimum 50"
    // error. Re-apply the floor once the theme settles. ensureMin only ever
    // raises to the door minimum, so these passes are safe; applyPresetQty
    // rides the same schedule (see its stand-down guard above).
    requestAnimationFrame(function () { ensureMin(); applyPresetQty(); render(); });
    setTimeout(function () { ensureMin(); applyPresetQty(); render(); }, 400);

    // The timed passes above lose a race when the cart already holds this variant:
    // product-info's setQuantityBoundries() runs again after a NETWORK fetch of the
    // section's quantity rules and hard-sets input.value back to its computed min
    // (1). It publishes 'quantity-update' after every reset, so re-asserting on
    // that event always lands after Dawn does.
    if (typeof subscribe === 'function') {
      var qtyEvent = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.quantityUpdate) || 'quantity-update';
      subscribe(qtyEvent, function () { ensureMin(); applyPresetQty(); render(); });
    }

    // Eurus adapter: the theme's +/- steppers update the qty through Alpine
    // x-model, which writes input.value SILENTLY — no input/change event ever
    // fires on the element, so the render listeners above never hear steppers.
    // The stepper's only signal is this document-level event. A stepper click
    // is USER INTENT: the ?qty= preset stands down here, same as a trusted
    // keystroke — never re-assert the preset over it.
    if (ADAPTER.qtyChangedEvent) {
      document.addEventListener(ADAPTER.qtyChangedEvent, function (e) {
        presetQty = 0;
        // The event fires BEFORE Alpine flushes the new qty into input.value
        // (x-model writes on the next effect tick), so reading the input here
        // yields the PREVIOUS quantity. detail.qty carries the authoritative
        // new value; write it through the single write path.
        var n = e && e.detail && parseInt(e.detail.qty, 10);
        if (n) setQtyValue(n);
        if (n && availableQty != null && n > availableQty) {
          // Stock cap vs stepper (2026-08-01): Alpine ignores the input's max
          // attribute and flushes the over-stock value AFTER this event, so the
          // synchronous clamp above no-ops against the pre-flush value.
          // Re-assert on a macrotask — past Alpine's microtask flush.
          setTimeout(function () { setQtyValue(availableQty); render(); }, 0);
        }
        ensureMin();
        render();
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // v1 limitation: Dawn re-renders the product form on variant change, which can drop
  // this panel. Greeting cards are single-variant, so fine for now; re-init on Dawn's
  // variant/section-render event in a later pass for variant-heavy products.
})();
